import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Legend,
  Tooltip,
} from "chart.js";
import { Chart as ChartComponent } from "react-chartjs-2";
import { analyzeTests } from "./analyze-tests";

Chart.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Legend, Tooltip);

function KpiCard({ value, label, color }) {
  return (
    <Card sx={{ flex: 1, textAlign: "center" }}>
      <CardContent sx={{ py: 2 }}>
        <Typography variant="h4" fontWeight={700} color={color || "primary"}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}

const parallelChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "top" },
    tooltip: { mode: "index" },
  },
  scales: {
    y: {
      beginAtZero: true,
      stacked: true,
      ticks: { stepSize: 1, precision: 0 },
      title: { display: true, text: "Nombre d'essais" },
    },
    x: { stacked: true, ticks: { maxRotation: 45 } },
  },
};

const waitChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    y: {
      beginAtZero: true,
      ticks: { stepSize: 1, precision: 0 },
      title: { display: true, text: "Nombre d'essais" },
    },
    x: { title: { display: true, text: "Semaines d'attente" } },
  },
};

const durationChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "top" },
    tooltip: { mode: "index" },
  },
  scales: {
    y: {
      beginAtZero: true,
      stacked: true,
      ticks: { stepSize: 1, precision: 0 },
      title: { display: true, text: "Nombre d'essais" },
    },
    x: {
      stacked: true,
      title: { display: true, text: "Durée (semaines)" },
    },
  },
};

export default function TrialsTab({ testsRows, contactsRows, contactsProRows, clientsRows }) {
  const analysis = useMemo(() => {
    if (!testsRows || testsRows.length === 0) return null;
    return analyzeTests(testsRows, contactsRows, contactsProRows, clientsRows);
  }, [testsRows, contactsRows, contactsProRows, clientsRows]);

  if (!analysis) {
    return (
      <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
        Aucune donnée d'essais disponible.
      </Typography>
    );
  }

  const { parallel, waitingTime, duration, conversion } = analysis;

  return (
    <Box>
      {/* === Parallel Trials === */}
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        Essais parallèles
      </Typography>

      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
        <KpiCard value={parallel.kpis.totalTests} label="Essais avec période" />
        <KpiCard
          value={parallel.kpis.peakCount}
          label={`Max parallèles (${parallel.kpis.peakMonth})`}
          color="success.main"
        />
        <KpiCard value={parallel.kpis.avgParallel} label="Moyenne par mois" />
        <KpiCard value={parallel.kpis.skippedCount} label="Sans période" />
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box>
            <ChartComponent
              type="bar"
              height={400}
              data={{ labels: parallel.labels, datasets: parallel.datasets }}
              options={parallelChartOptions}
            />
          </Box>
          {parallel.kpis.skippedCount > 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1.5, fontStyle: "italic" }}
            >
              {parallel.kpis.skippedCount} essais sans période de location :{" "}
              {parallel.skippedNames.join(", ")}.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* === Waiting Time === */}
      <Typography variant="h6" sx={{ mt: 4, mb: 1.5 }}>
        Temps d'attente (2024+) — demande → début essai
      </Typography>

      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
        <KpiCard
          value={waitingTime.kpis.waitTotal}
          label="Essais avec les deux dates"
        />
        <KpiCard
          value={`${waitingTime.kpis.waitMedian} sem.`}
          label="Médiane"
          color="success.main"
        />
        <KpiCard value={`${waitingTime.kpis.waitMean} sem.`} label="Moyenne" />
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box>
            <ChartComponent
              type="bar"
              height={400}
              data={{
                labels: waitingTime.labels,
                datasets: [
                  {
                    label: "Essais",
                    data: waitingTime.data,
                    backgroundColor: "rgba(99,102,241,.7)",
                    borderRadius: 4,
                  },
                ],
              }}
              options={waitChartOptions}
            />
          </Box>
          {(waitingTime.skipParts.length > 0 || waitingTime.excludedLongWait > 0) && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1.5, fontStyle: "italic" }}
            >
              Sur {waitingTime.totalRows} essais (2024+) :{" "}
              {waitingTime.kpis.waitTotal} avec les deux dates
              {waitingTime.skippedTotal > 0 && (
                <>, {waitingTime.skippedTotal} exclus ({waitingTime.skipParts.join(", ")})</>
              )}
              {waitingTime.excludedLongWait > 0 && (
                <>, {waitingTime.excludedLongWait} exclus (attente &gt; 6 mois)</>
              )}
              .
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* === Duration Distribution === */}
      <Typography variant="h6" sx={{ mt: 4, mb: 1.5 }}>
        Durée des essais
      </Typography>

      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
        <KpiCard value={duration.kpis.total} label="Essais avec période" />
        <KpiCard
          value={`${duration.kpis.median} sem.`}
          label="Médiane"
          color="success.main"
        />
        <KpiCard value={`${duration.kpis.mean} sem.`} label="Moyenne" />
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box>
            <ChartComponent
              type="bar"
              height={400}
              data={{ labels: duration.labels, datasets: duration.datasets }}
              options={durationChartOptions}
            />
          </Box>
          {duration.excluded > 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1.5, fontStyle: "italic" }}
            >
              {duration.excluded} essais exclus (durée &gt; 6 mois).
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* === Conversion: Tests → Clients === */}
      <Typography variant="h6" sx={{ mt: 4, mb: 1.5 }}>
        Conversion essais → clients
      </Typography>

      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
        <KpiCard value={conversion.total} label="Essais avec période" />
        <KpiCard
          value={conversion.converted}
          label={`Convertis (${conversion.rate}%)`}
          color="success.main"
        />
        <KpiCard
          value={conversion.convertedByClientType.Particulier}
          label="Clients particuliers"
        />
        <KpiCard
          value={conversion.convertedByClientType.Pro}
          label="Clients pros"
          color="warning.main"
        />
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Par origine du contact
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Origine</TableCell>
                  <TableCell align="right">Essais</TableCell>
                  <TableCell align="right">Convertis</TableCell>
                  <TableCell align="right">Taux</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {["Particulier", "Pro", "Inconnu"].map((t) => {
                  const d = conversion.byType[t];
                  if (!d || d.total === 0) return null;
                  const rate =
                    d.total > 0
                      ? `${Math.round((d.converted / d.total) * 100)}%`
                      : "-";
                  return (
                    <TableRow key={t}>
                      <TableCell>{t}</TableCell>
                      <TableCell align="right">{d.total}</TableCell>
                      <TableCell align="right">{d.converted}</TableCell>
                      <TableCell align="right">{rate}</TableCell>
                    </TableRow>
                  );
                })}
                <TableRow sx={{ "& td": { fontWeight: 600 } }}>
                  <TableCell>Total</TableCell>
                  <TableCell align="right">{conversion.total}</TableCell>
                  <TableCell align="right">{conversion.converted}</TableCell>
                  <TableCell align="right">{conversion.rate}%</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1.5, fontStyle: "italic" }}
          >
            Particulier / Pro = déduit des boards Contacts, Contacts Pro, ou
            du champ Type dans Clients. Inconnu = absent des trois boards.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
